import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type { AttendanceMonthSummary } from "@/containers/staff/staff-attendance/attendance-types";
import { formatAttendanceHoursLabel } from "@/containers/staff/staff-attendance/attendance-cell-display";

type Props = {
  summary: AttendanceMonthSummary | null;
  includeLate: boolean;
};

type CardDef = {
  key: string;
  label: string;
  value: string | number;
  tone?: "success" | "warning" | "error" | "info" | "default";
};

function toneSx(tone: CardDef["tone"]) {
  return (theme: import("@mui/material/styles").Theme) => {
    switch (tone) {
      case "success":
        return {
          bgcolor: alpha(theme.palette.success.main, 0.1),
          borderColor: alpha(theme.palette.success.main, 0.35),
        };
      case "warning":
        return {
          bgcolor: alpha(theme.palette.warning.main, 0.12),
          borderColor: alpha(theme.palette.warning.main, 0.4),
        };
      case "error":
        return {
          bgcolor: alpha(theme.palette.error.main, 0.1),
          borderColor: alpha(theme.palette.error.main, 0.35),
        };
      case "info":
        return {
          bgcolor: alpha(theme.palette.info.main, 0.1),
          borderColor: alpha(theme.palette.info.main, 0.35),
        };
      default:
        return {
          bgcolor: "background.paper",
          borderColor: "divider",
        };
    }
  };
}

export function StaffAttendanceSummaryCards({ summary, includeLate }: Props) {
  const cards: CardDef[] = [
    {
      key: "present",
      label: "Present",
      value: summary?.presentDays ?? "—",
      tone: "success",
    },
    {
      key: "incomplete",
      label: "Incomplete",
      value: summary?.incompleteDays ?? "—",
      tone: "warning",
    },
    {
      key: "absent",
      label: "Absent",
      value: summary?.absentDays ?? "—",
      tone: "error",
    },
    {
      key: "leave",
      label: "Leave",
      value: summary?.leaveDays ?? "—",
      tone: "info",
    },
    {
      key: "hours",
      label: "Hours",
      value: summary != null ? formatAttendanceHoursLabel(summary.totalHours) : "—",
    },
  ];

  if (includeLate) {
    cards.push({
      key: "late",
      label: "Late",
      value: summary?.lateDays ?? "—",
      tone: "warning",
    });
  }

  cards.push({
    key: "working",
    label: "Working days",
    value: summary?.workingDaysInMonth ?? "—",
  });

  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ width: "100%" }}>
      {cards.map((card) => (
        <Box
          key={card.key}
          sx={[
            toneSx(card.tone),
            {
              border: 1,
              borderRadius: 1,
              px: 2,
              py: 1.5,
              minWidth: 112,
              flex: "1 1 112px",
            },
          ]}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {card.label}
          </Typography>
          <Typography variant="h6" component="div" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {card.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
